import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
  Park,
  Spa,
  Terrain,
  Pets,
  WarningAmber,
  Fingerprint,
  HelpOutline,
  InfoOutlined,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Site } from 'src/api/Api';
import { ProjectWithSites } from 'src/state-management/state';
import {
  ScorecardIndicator,
  getIndicator,
  formatIndicatorValue,
  NATURE_ATLAS_SECTIONS,
  CONCERN_LABELS,
  ConcernLevel,
} from 'src/resources/mock/gundre_scorecard_data';
import OverviewTab from './components/OverviewTab';

// Colors a gauge/bar by how far along its scale the value sits (fallback when no known concern level exists).
const scoreColor = (pct: number): string => (pct >= 0.8 ? '#10b981' : pct >= 0.5 ? '#f59e0b' : '#ef4444');

// Concern colour rule for this panel: very-low and low read green, moderate amber, high and very-high red.
const concernColor = (concern: ConcernLevel): string =>
  concern === 'very-low' || concern === 'low' ? '#22c55e'
    : concern === 'moderate' ? '#eab308'
      : '#ef4444';

// Concern level per metric for the Tata Motors site, whose scorecard carries no reference baseline.
const TATA_CONCERN: Record<string, ConcernLevel> = {
  'Global Human Modification': 'high',
  'Human Disturbance Index': 'very-high',
  'Light Pollution (VIIRS)': 'moderate',
  'Natural Habitat Extent': 'low',
  'Natural Land Cover Proportion': 'low',
  'Threatened Species Richness': 'very-low',
  'KBA/IBA Overlap': 'very-low',
  'Biodiversity Intactness Index': 'moderate',
  'Vegetation Structure (NDVI)': 'very-low',
  'Habitat Health Index (HHI)': 'low',
  'Aridity Index': 'moderate',
  'Leaf Area Index (MODIS MCD15A3H)': 'very-low',
};

// "POINT (lng lat)" -> { lat, lng }
const parsePointWkt = (wkt?: string | null): { lat: number; lng: number } | null => {
  const m = /POINT\s*\(\s*([-.\d]+)\s+([-.\d]+)\s*\)/i.exec(wkt || '');
  if (!m) return null;
  const lng = parseFloat(m[1]);
  const lat = parseFloat(m[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

const oneDecimal = (value: number): number => Math.round(value * 10) / 10;

interface MeteoCurrent {
  time?: string;
  temperature_2m?: number;
  relative_humidity_2m?: number;
  precipitation?: number;
  pressure_msl?: number;
}

// US EPA AQI breakpoints.
const aqiBand = (aqi: number): string => {
  if (aqi <= 50) return 'Good';
  if (aqi <= 100) return 'Moderate';
  if (aqi <= 150) return 'Unhealthy for sensitive groups';
  if (aqi <= 200) return 'Unhealthy';
  if (aqi <= 300) return 'Very unhealthy';
  return 'Hazardous';
};

// Renders nothing unless the forecast call succeeds; each air row degrades to its own em dash.
const LiveClimateCard: FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const [current, setCurrent] = useState<MeteoCurrent | null>(null);
  const [aqi, setAqi] = useState<number | null>(null);
  const [pm25, setPm25] = useState<number | null>(null);

  useEffect(() => {
    const where = `latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&timezone=auto`;
    let cancelled = false;
    let lastFetch = 0;
    const active = new Set<AbortController>();

    const load = () => {
      lastFetch = Date.now();
      const forecast = new AbortController();
      const air = new AbortController();
      active.add(forecast).add(air);
      fetch(
        `https://api.open-meteo.com/v1/forecast?${where}&current=temperature_2m,relative_humidity_2m,precipitation,pressure_msl`,
        { signal: forecast.signal },
      )
        .then(res => (res.ok ? res.json() : null))
        .then(body => {
          if (!cancelled) setCurrent(body?.current ?? null);
        })
        .catch(() => {
          if (!cancelled && !forecast.signal.aborted) setCurrent(null);
        })
        .finally(() => active.delete(forecast));
      fetch(`https://air-quality-api.open-meteo.com/v1/air-quality?${where}&current=us_aqi,pm2_5`, {
        signal: air.signal,
      })
        .then(res => (res.ok ? res.json() : null))
        .then(body => {
          const a = body?.current?.us_aqi as number | undefined;
          const pm = body?.current?.pm2_5 as number | undefined;
          if (!cancelled) {
            setAqi(a ?? null);
            setPm25(pm ?? null);
          }
        })
        .catch(() => {
          if (!cancelled && !air.signal.aborted) { setAqi(null); setPm25(null); }
        })
        .finally(() => active.delete(air));
    };

    load();

    // Anchor each refresh to the top of the hour so the card turns over with Open-Meteo's hourly
    // `current` value instead of drifting an hour later on every reload.
    const msToNextHour = 3_600_000 - (Date.now() % 3_600_000);
    let hourly: ReturnType<typeof setInterval> | undefined;
    const align = setTimeout(() => {
      load();
      hourly = setInterval(load, 3_600_000);
    }, msToNextHour);

    // Browsers freeze timers while the tab is hidden or the machine sleeps, so resync on return
    // once the reading has gone a full hour stale.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetch >= 3_600_000) load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(align);
      if (hourly) clearInterval(hourly);
      document.removeEventListener('visibilitychange', onVisible);
      active.forEach(c => c.abort());
    };
  }, [lat, lng]);

  if (!current) return null;

  const measure = (label: string, value: number | undefined, unit: string): [string, string] =>
    value == null ? [label, '—'] : [label, `${oneDecimal(value)} ${unit}`];

  const rows: [string, string][] = [
    measure('Temperature', current.temperature_2m, '°C'),
    measure('Humidity', current.relative_humidity_2m, '%'),
    measure('Rainfall', current.precipitation, 'mm'),
    ['Air Quality', aqi == null ? '—' : `${aqi} · ${aqiBand(aqi)}`],
    ['PM2.5', pm25 == null ? '—' : `${oneDecimal(pm25)} µg/m³`],
    measure('Pressure', current.pressure_msl, 'hPa'),
  ];

  return (
    <Box className="insights-card">
      <Box className="insight-group">
        <Box className="threats-list-card">
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.38)', mb: 0.75 }}>
            Refreshed every hour
          </Typography>
          <span className="threats-heading">Current Climate Readings</span>
          {rows.map(([label, value]) => (
            <Box className="threat-item-row" sx={{ gap: '10px' }} key={label}>
              <span
                className="threat-title-txt"
                style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px', letterSpacing: '0.3px', flexShrink: 0 }}
              >
                {label}
              </span>
              <span
                className="threat-title-txt"
                style={{ color: '#ffffff', fontWeight: 700, fontSize: '13px', textAlign: 'right', flex: '1 1 auto', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
              >
                {value}
              </span>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Self-contained indicator row — site value plus a magnitude bar chart, color-coded by concern
// level where a reference concern level is known. Styled entirely via sx/inline styles so it
// never depends on a parent's CSS class scope.

const METRIC_DESCRIPTIONS: Record<string, string> = {
  'Threatened Species Richness': 'Represents the number of threatened species whose documented or modelled geographic distribution overlaps the assessed ecoregion. Higher values indicate greater potential conservation sensitivity, but do not confirm species occurrence within the assessment area.',
  'KBA/IBA Overlap': 'Measures the proportion of the site that overlaps with a Key Biodiversity Area or Important Bird Area. Higher concern represents greater conservation sensitivity rather than poorer ecosystem condition.',
  'Biodiversity Intactness Index': 'Measures how much native biodiversity abundance is retained relative to an undisturbed reference state. Higher values indicate greater biodiversity intactness.',
  'Vegetation Structure (NDVI)': 'Measures vegetation greenness and photosynthetic activity across the site. Higher values generally indicate greater photosynthetically active vegetation and canopy greenness; interpretation should consider seasonality, land-cover type and reference conditions.',
  'Habitat Health Index (HHI)': 'Measures habitat stability and consistency using vegetation performance and variability. Higher values indicate more stable and better performing habitat condition.',
  'Aridity Index': 'Measures climatic moisture balance as the ratio of precipitation to potential evapotranspiration. Higher values generally indicate lower aridity and better moisture availability.',
  'Leaf Area Index (MODIS MCD15A3H)': 'LAI is interpreted here as a structural canopy indicator rather than a direct measure of overall vegetation abundance; therefore, high LAI should not automatically be interpreted as high biodiversity condition.',
  'Global Human Modification': 'Measures the cumulative human modification of terrestrial lands, combining various stressors like human settlement, agriculture, transportation, mining, and energy production.',
  'Light Pollution (VIIRS)': 'Measures artificial light emissions at night, which can disrupt ecological processes and indicate human presence.',
  'Human Disturbance Index': 'Measures the extent of human influence on the landscape based on various pressure factors.',
  'Natural Land Cover Proportion': 'Proportion of the site that is covered by natural land classes as opposed to human-modified areas.',
  'Landscape Connectivity (CPLAND)': 'Measures the degree to which landscapes facilitate or impede movement among resource patches.',
  'Natural Habitat Extent': 'Measures the total area of natural habitat remaining within the site.',
};

const customTooltipProps = {
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: 'rgba(28,31,38,0.95)',
        border: '1px solid rgba(255,255,255,0.1)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        padding: '12px',
        borderRadius: '8px',
        maxWidth: 280,
      }
    },
    arrow: {
      sx: {
        color: 'rgba(28,31,38,0.95)',
        '&::before': {
          border: '1px solid rgba(255,255,255,0.1)',
        }
      }
    }
  }
};

const TataIndicatorCard: FC<{ row: ScorecardIndicator; overrideName?: string; abbr?: string; description?: string; hideFullName?: boolean; popupHeading?: string }> = ({ row, overrideName, abbr, description, hideFullName, popupHeading }) => {
  // A metric with no concern level gets no colour at all, not a score-derived one.
  const barColor = row.concern ? concernColor(row.concern) : '#9ca3af';
  const pct = row.value !== null ? Math.max(0, Math.min(1, row.value / row.gaugeMax)) : 0;
  
  const displayText = abbr || overrideName || row.name;

  return (
    <Box
      sx={{
        background: `linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)`,
        border: '1px solid rgba(255,255,255,0.08)',
        borderLeft: `4px solid ${barColor}`,
        borderRadius: '8px',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          background: `linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 100%)`,
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 16px ${barColor}20`,
          borderColor: 'rgba(255,255,255,0.15)',
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.95)', letterSpacing: '0.3px', display: 'flex', alignItems: 'center' }}>
            {displayText}
            {description && (
              <Tooltip 
                title={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>
                      {popupHeading || overrideName || row.name}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                      {description}
                    </Typography>
                  </Box>
                } 
                arrow 
                placement="top"
                {...customTooltipProps}
              >
                <InfoOutlined sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', '&:hover': { color: '#fff' }, ml: '6px' }} />
              </Tooltip>
            )}
          </Typography>
          {row.concern && (
            <Typography sx={{ fontSize: '10px', color: barColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {CONCERN_LABELS[row.concern]}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: row.concern ? barColor : '#fff', lineHeight: 1, flexShrink: 0 }}>
          {formatIndicatorValue(row)}
        </Typography>
      </Box>

      {/* Subtle Progress Bar */}
      <Box sx={{ width: '100%', height: '3px', bgcolor: 'rgba(255, 255, 255, 0.06)', borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.round(pct * 100)}%`, height: '100%', bgcolor: barColor, borderRadius: '2px', transition: 'width 0.4s ease' }} />
      </Box>
    </Box>
  );
};

const IndicatorCard: FC<{ row: ScorecardIndicator; popupHeading?: string }> = ({ row, popupHeading }) => {
  const pct = row.value !== null ? Math.max(0, Math.min(1, row.value / row.gaugeMax)) : 0;
  const barColor = row.concern ? concernColor(row.concern) : scoreColor(pct);
  const description = METRIC_DESCRIPTIONS[row.name];
  return (
    <Box
      sx={{
        background: `linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)`,
        border: '1px solid rgba(255,255,255,0.05)',
        borderLeft: `3px solid ${barColor}`,
        borderRadius: '8px',
        padding: '12px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          background: `linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 100%)`,
          borderColor: 'rgba(255,255,255,0.15)',
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            color: 'rgba(255,255,255,0.85)',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {row.name}
          {description && (
            <Tooltip 
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>
                    {popupHeading || row.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                    {description}
                  </Typography>
                </Box>
              } 
              arrow 
              placement="top"
              {...customTooltipProps}
            >
              <InfoOutlined sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', '&:hover': { color: '#fff' }, ml: '6px' }} />
            </Tooltip>
          )}
        </Typography>
        {row.concern && (
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.3px',
              color: barColor,
              textTransform: 'uppercase',
            }}
          >
            {CONCERN_LABELS[row.concern]}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: barColor, lineHeight: 1.1 }}>
        {formatIndicatorValue(row)}
      </Typography>
    </Box>
  );
};

interface RightPanelProps {
  projectDetails: ProjectWithSites;
  isSiteLevel: boolean;
  selectedSite: Site | null;
  activeRightTab: 'all' | 'nature' | 'climate';
  setActiveRightTab: (tab: 'all' | 'nature' | 'climate') => void;
  bioSummary: Record<string, any> | null;
  envSummary: Record<string, any> | null;
  overviewSummary: Record<string, any> | null;
  onResetProjectView: () => void;
  isCorbettProject: boolean;
  scorecard?: ScorecardIndicator[] | null;
  width?: number;
}



const TATA_METRIC_CATEGORIES = [
  {
    name: "Biodiversity",
    image: "/biodiversity.png",
    metrics: [
      { real: 'Natural Land Cover Proportion', display: 'Natural Landcover', abbr: 'NATURAL LANDCOVER', subcategory: 'habitat' },
      { real: 'Threatened Species Richness', display: 'Threatened Richness', abbr: 'THREATENED RICHNESS', subcategory: 'species' },
      { real: 'KBA/IBA Overlap', display: 'Key Biodiversity Areas Overlap', abbr: 'KBA OVERLAP', subcategory: 'species' },
      { real: 'Biodiversity Intactness Index', display: 'Biodiversity Intactness Index', abbr: 'BII', subcategory: 'species' },
      { real: 'Habitat Health Index (HHI)', display: 'Habitat Health', abbr: 'HHI', subcategory: 'vegetation' },
      { real: 'Landscape Connectivity (CPLAND)', display: 'Land Fragmentation', abbr: 'CPLAND', subcategory: 'habitat' },
      { real: 'Natural Habitat Extent', display: 'Natural Habitat', abbr: 'NHE', subcategory: 'habitat' },
      { real: 'Leaf Area Index (MODIS MCD15A3H)', display: 'Leaf Area Index', abbr: 'LAI', subcategory: 'vegetation' },
      { real: 'Vegetation Structure (NDVI)', display: 'Vegetation Structure', abbr: 'NDVI', subcategory: 'vegetation' },
    ]
  },
  {
    name: "Human Impact",
    image: "/humanImpact.png",
    metrics: [
      { real: 'Global Human Modification', display: 'Global Human Modification', abbr: 'GHM' },
      { real: 'Light Pollution (VIIRS)', display: 'Light Pollution', abbr: 'LIGHT POLLUTION' },
      { real: 'Human Disturbance Index', display: 'Human Disturbance Index', abbr: 'HDI' },
    ]
  }
];

const BIO_SUB_SECTIONS = [
  { id: 'habitat', title: 'Habitat & Landscape', icon: Park },
  { id: 'species', title: 'Species & Protection', icon: Pets },
  { id: 'vegetation', title: 'Vegetation & Eco-Health', icon: Spa },
];

const TATA_CLIMATE_METRICS = [
  { real: 'Aridity Index', display: 'Aridity Index', abbr: 'ARIDITY INDEX' },
];

const RightPanel: FC<RightPanelProps> = ({
  projectDetails,
  isSiteLevel,
  selectedSite,
  activeRightTab,
  setActiveRightTab,
  bioSummary,
  envSummary,
  overviewSummary,
  onResetProjectView,
  isCorbettProject,
  scorecard,
  width,
}) => {
  const [activeNatureCategory, setActiveNatureCategory] = useState<string | null>(null);
  const [bioSubcategory, setBioSubcategory] = useState<'all' | 'habitat' | 'species' | 'vegetation'>('all');

  const climateCoords = useMemo(() => {
    const sitePoint = parsePointWkt(selectedSite?.centroid_wkt);
    if (isSiteLevel && sitePoint) return sitePoint;
    const points = (projectDetails.sites || [])
      .map(s => parsePointWkt(s.centroid_wkt))
      .filter((p): p is { lat: number; lng: number } => !!p);
    if (points.length === 0) return null;
    return {
      lat: points.reduce((acc, p) => acc + p.lat, 0) / points.length,
      lng: points.reduce((acc, p) => acc + p.lng, 0) / points.length,
    };
  }, [isSiteLevel, selectedSite, projectDetails.sites]);

  const getMetricIndicator = (name: string): ScorecardIndicator => {
     let base = scorecard ? getIndicator(scorecard, name) : null;
     if (!base) {
         base = {
           name,
           value: null,
           tier1: null,
           tier2: null,
           unit: 'index',
           decimals: 2,
           gaugeMax: 1,
           concern: null,
         };
         if (name === 'Light Pollution (VIIRS)') base.gaugeMax = 100;
         if (name === 'Threatened Species Richness') { base.unit = 'count'; base.gaugeMax = 50; }
         if (name === 'Habitat Health Index (HHI)') base.gaugeMax = 5;
         if (name === 'Leaf Area Index (MODIS MCD15A3H)') base.gaugeMax = 3;
         if (name.includes('Temperature')) { base.unit = 'temp'; base.decimals = 1; base.gaugeMax = 35; }
         if (name.includes('Extent') || name.includes('Proportion') || name.includes('Overlap') || name.includes('CPLAND')) { base.unit = 'percent'; base.decimals = 1; base.gaugeMax = 100; }
     }
     if (isTataMotors) {
       const v = TATA_MOTORS_DATA[name];
       if (v !== undefined) return { ...base, value: v, concern: TATA_CONCERN[name] ?? base.concern };
     }
     return base;
  };






  const isTataMotors = projectDetails?.project_name?.toLowerCase().includes('tata motors');
  const TATA_MOTORS_DATA: Record<string, number | null> = {
    'Global Human Modification': 0.7789,
    'Light Pollution (VIIRS)': 25.9731,
    'Human Disturbance Index': 0.9206,
    'Natural Habitat Extent': 74.4093,
    'Natural Land Cover Proportion': 72.2266,
    'Landscape Connectivity (CPLAND)': 59.8930,
    'Threatened Species Richness': 25,
    'KBA/IBA Overlap': 0,
    'Biodiversity Intactness Index': 0.4536,
    'Vegetation Structure (NDVI)': 0.4428,
    'Habitat Health Index (HHI)': 2.5206,
    'Aridity Index': 0.7667,
    'Leaf Area Index (MODIS MCD15A3H)': 0.5709
  };



  return (
    <Box className="right-panel glass-panel" style={{ width: width !== undefined ? width : undefined, minWidth: width !== undefined ? width : undefined, maxWidth: width !== undefined ? width : undefined }}>
      {/* Horizontal Section Switch Options — sticky */}
      <Box className="tabs-header-sticky-wrapper">
        <Box className="tabs-header-horizontal">
          <button
            className={`tab-item-btn-h ${activeRightTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveRightTab('all')}
          >
            Overview
          </button>
          <button
            className={`tab-item-btn-h ${activeRightTab === 'nature' ? 'active' : ''}`}
            onClick={() => setActiveRightTab('nature')}
          >
            Nature Atlas
          </button>
          <button
            className={`tab-item-btn-h ${activeRightTab === 'climate' ? 'active' : ''}`}
            onClick={() => setActiveRightTab('climate')}
          >
            Climate
          </button>
        </Box>
      </Box>

      {/* Site context banner — shows when a site is selected */}
      {isSiteLevel && selectedSite && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.2)',
            borderRadius: '6px',
            px: 1.5,
            py: 0.75,
            mb: 1.5,
            gap: 1,
          }}
        >
          <Typography
            sx={{ fontSize: '10px', color: '#10b981', fontWeight: 700, letterSpacing: '0.4px' }}
          >
            📍 {selectedSite.site_name}
          </Typography>
          <button
            type="button"
            onClick={onResetProjectView}
            style={{
              fontSize: '9px',
              color: 'rgba(255,255,255,0.5)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            ✕ Project view
          </button>
        </Box>
      )}

      {/* "All" Tab — Project Operational Metrics */}
      {activeRightTab === 'all' && (
        <OverviewTab 
          projectDetails={projectDetails}
          isSiteLevel={isSiteLevel}
          selectedSite={selectedSite}
          bioSummary={bioSummary}
          overviewSummary={overviewSummary}
          isTataMotors={isTataMotors}
        />
      )}

      {/* Nature Atlas Tab — Three Section Redesign */}
      {activeRightTab === 'nature' && isTataMotors && (
        <Box className="nature-atlas-panel-wrapper" sx={{ p: 2, pt: 4, mt: 3, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {!activeNatureCategory ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '16px', 
              width: '100%',
            }}>
              {TATA_METRIC_CATEGORIES.map(cat => (
                <Box
                  key={cat.name}
                  onClick={() => setActiveNatureCategory(cat.name)}
                  sx={{
                    minHeight: '230px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '20px',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    backgroundImage: `linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.85) 55%, rgba(15, 23, 42, 0.98) 100%), url(${cat.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(16, 185, 129, 0.5)',
                      boxShadow: '0 12px 30px rgba(16, 185, 129, 0.25)',
                      '& .view-metrics-btn': {
                        background: '#10B981',
                        borderColor: '#10B981',
                        color: '#000',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                        transform: 'translateY(-1px)',
                      }
                    }
                  }}
                >
                  {/* Top Header: Category Name & Tag */}
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ 
                      fontSize: '20px', 
                      fontWeight: 800, 
                      letterSpacing: '0.5px', 
                      textTransform: 'uppercase',
                      textShadow: '0px 2px 8px rgba(0,0,0,0.8)' 
                    }}>
                      {cat.name}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      background: 'rgba(255, 255, 255, 0.12)',
                      backdropFilter: 'blur(8px)',
                      px: 1,
                      py: 0.3,
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.15)'
                    }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {cat.metrics.length} Metrics
                      </Typography>
                    </Box>
                  </Box>

                  {/* Middle: Frosted Mini-Scorecard Container */}
                  <Box sx={{ 
                    position: 'relative', 
                    zIndex: 1, 
                    my: 1.5,
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '6px',
                    background: 'rgba(0, 0, 0, 0.45)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    p: '10px 12px',
                  }}>
                    {cat.metrics.slice(0, 3).map((metric, i) => {
                      const indicator = getMetricIndicator(metric.real);
                      const formattedVal = formatIndicatorValue(indicator);
                      return (
                        <Box 
                          key={i} 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            py: 0.25,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, pr: 1 }}>
                            <Box sx={{ width: '5px', height: '5px', borderRadius: '50%', bgcolor: '#10B981', flexShrink: 0, boxShadow: '0 0 6px #10B981' }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {metric.display}
                            </Typography>
                          </Box>
                          <Box sx={{
                            background: 'rgba(255, 255, 255, 0.06)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '6px',
                            px: 0.75,
                            py: 0.2,
                            flexShrink: 0,
                          }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#10B981', lineHeight: 1.2 }}>
                              {formattedVal}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Bottom: Action Pill Button */}
                  <Box 
                    className="view-metrics-btn"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.85,
                      color: '#fff', 
                      fontSize: '11px', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.8px',
                      transition: 'all 0.25s ease',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <span>View Metrics</span>
                    <ArrowForwardIcon sx={{ fontSize: 15 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <Box 
                  onClick={() => {
                    setActiveNatureCategory(null);
                    setBioSubcategory('all');
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: 'rgba(255,255,255,0.65)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    marginLeft: '-8px',
                    '&:hover': {
                      color: '#fff',
                      background: 'rgba(255,255,255,0.08)'
                    }
                  }}
                >
                  <span style={{ fontSize: '18px', lineHeight: 1 }}>&larr;</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Back</span>
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {activeNatureCategory}
                </Typography>
              </Box>

              {/* Biodiversity Subcategory Tabs */}
              {activeNatureCategory === 'Biodiversity' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Subcategory Filter Pills */}
                  <Box sx={{ display: 'flex', gap: '6px', overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
                    {[
                      { id: 'all', label: 'All', count: 9 },
                      { id: 'habitat', label: 'Habitat', count: 3 },
                      { id: 'species', label: 'Species', count: 3 },
                      { id: 'vegetation', label: 'Vegetation', count: 3 },
                    ].map((sub) => {
                      const active = bioSubcategory === sub.id;
                      return (
                        <Box
                          key={sub.id}
                          onClick={() => setBioSubcategory(sub.id as any)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            background: active ? '#10B981' : 'rgba(255, 255, 255, 0.05)',
                            color: active ? '#000' : 'rgba(255, 255, 255, 0.75)',
                            border: `1px solid ${active ? '#10B981' : 'rgba(255, 255, 255, 0.1)'}`,
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              borderColor: active ? '#10B981' : 'rgba(255, 255, 255, 0.3)',
                              color: active ? '#000' : '#fff',
                            }
                          }}
                        >
                          <span>{sub.label}</span>
                          <span style={{ fontSize: '10px', opacity: active ? 0.8 : 0.6 }}>({sub.count})</span>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Metrics Rendering */}
              {activeNatureCategory === 'Biodiversity' && bioSubcategory === 'all' ? (
                // Grouped into Sections for All view
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {BIO_SUB_SECTIONS.map((sec) => {
                    const currentCategoryObj = TATA_METRIC_CATEGORIES.find(c => c.name === 'Biodiversity');
                    const sectionMetrics = (currentCategoryObj?.metrics || []).filter(m => (m as any).subcategory === sec.id);
                    const Icon = sec.icon;
                    return (
                      <Box key={sec.id} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: 0.5 }}>
                          <Icon sx={{ fontSize: 16, color: '#10B981' }} />
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {sec.title}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.4)', ml: 'auto' }}>
                            {sectionMetrics.length} indicators
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {sectionMetrics.map((n) => {
                            const r = getMetricIndicator(n.real);
                            if (!r) return null;
                            const popupHeading = n.real === 'Vegetation Structure (NDVI)' ? 'Normalized Difference Vegetation Index' : undefined;
                            return <TataIndicatorCard row={{ ...r, name: n.display }} abbr={n.abbr} hideFullName={(n as any).hideFullName} description={METRIC_DESCRIPTIONS[n.real]} key={n.display} popupHeading={popupHeading} />;
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                // Filtered by subcategory or Human Impact
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(TATA_METRIC_CATEGORIES.find(c => c.name === activeNatureCategory)?.metrics || [])
                    .filter(m => activeNatureCategory !== 'Biodiversity' || (m as any).subcategory === bioSubcategory)
                    .map((n) => {
                      const r = getMetricIndicator(n.real);
                      if (!r) return null;
                      const popupHeading = n.real === 'Vegetation Structure (NDVI)' ? 'Normalized Difference Vegetation Index' : undefined;
                      return <TataIndicatorCard row={{ ...r, name: n.display }} abbr={n.abbr} hideFullName={(n as any).hideFullName} description={METRIC_DESCRIPTIONS[n.real]} key={n.display} popupHeading={popupHeading} />;
                    })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {activeRightTab === 'nature' && !isTataMotors && scorecard && (
        <Box className="nature-atlas-panel-wrapper">
          {NATURE_ATLAS_SECTIONS.map((section) => {
            const rows = section.indicatorNames
              .map((name) => getIndicator(scorecard, name))
              .filter((i): i is ScorecardIndicator => !!i && i.value !== null);
            if (rows.length === 0) return null;
            return (
              <Box className="nature-section" key={section.title}>
                <Box className="section-title-row">
                  <span className="title-text">{section.title.toUpperCase()}</span>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {rows.map((row) => (
                    <IndicatorCard row={row} key={row.name} popupHeading={row.name === 'Vegetation Structure (NDVI)' ? 'Normalized Difference Vegetation Index' : undefined} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {activeRightTab === 'nature' && !isTataMotors && !scorecard && (
        <Box className="nature-atlas-panel-wrapper">
          {/* ── 1. KEY INDICATORS ── */}
          <Box className="nature-section key-indicators-section">
            <Box className="section-title-row">
              <span>KEY INDICATORS</span>
            </Box>
            <Box className="indicators-grid-container">
              {/* Species Index */}
              <Box className="indicator-tile">
                <Box className="tile-top-row">
                  <span className="tile-name">Species Index</span>
                  {/* <button
                    type="button"
                    className="tile-more-link"
                    onClick={() => navigate(`/project/${projectDetails.project_id}/bio-diversity-dashboard`)}
                  >
                    More →
                  </button> */}
                </Box>
                <Box className="tile-value-row">
                  <span className="tile-main-val">--</span>
                  <span className="tile-trend-badge" style={{ background: 'transparent', color: 'inherit' }}>--</span>
                </Box>
                <div className="tile-sparkline-box">
                  <svg viewBox="0 0 80 20" width="100%" height="20" preserveAspectRatio="none">
                    <polyline
                      points="0,15 13,12 27,13 40,9 53,11 67,6 80,2"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="tile-baseline">Baseline: --</span>
              </Box>

              {/* Water Security */}
              <Box className="indicator-tile">
                <Box className="tile-top-row">
                  <span className="tile-name">Water Sec.</span>
                  {/* <button
                    type="button"
                    className="tile-more-link"
                    onClick={() => navigate(`/project/${projectDetails.project_id}/climate-dashboard`)}
                  >
                    More →
                  </button> */}
                </Box>
                <Box className="tile-value-row">
                  <span className="tile-main-val">--</span>
                  <span className="tile-trend-badge" style={{ background: 'transparent', color: 'inherit' }}>--</span>
                </Box>
                <div className="tile-sparkline-box">
                  <svg viewBox="0 0 80 20" width="100%" height="20" preserveAspectRatio="none">
                    <polyline
                      points="0,4 13,6 27,4 40,9 53,8 67,12 80,18"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="tile-baseline">Baseline: --</span>
              </Box>

              {/* Thermal Variation */}
              <Box className="indicator-tile">
                <Box className="tile-top-row">
                  <span className="tile-name">Thermal Var.</span>
                  {/* <button
                    type="button"
                    className="tile-more-link"
                    onClick={() => navigate(`/project/${projectDetails.project_id}/climate-dashboard`)}
                  >
                    More →
                  </button> */}
                </Box>
                <Box className="tile-value-row">
                  <span className="tile-main-val">--</span>
                  <span className="tile-trend-badge" style={{ background: 'transparent', color: 'inherit' }}>--</span>
                </Box>
                <div className="tile-sparkline-box">
                  <svg viewBox="0 0 80 20" width="100%" height="20" preserveAspectRatio="none">
                    <polyline
                      points="0,14 13,13 27,15 40,11 53,12 67,5 80,4"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span className="tile-baseline">Baseline: --</span>
              </Box>
            </Box>
          </Box>

          {/* ── 2. SPECIES DISTRIBUTION (EX-SITU) ── */}
          <Box className="nature-section species-dist-section">
            <Box className="section-title-row">
              <span className="title-text">SPECIES DISTRIBUTION (EX-SITU)</span>
              <HelpOutline
                className="info-icon"
                titleAccess="Ex-situ species distributions derived from regional ecological mapping"
              />
            </Box>

            <Box className="donut-chart-row-layout">
              {/* SVG Donut Chart */}
              <Box className="donut-chart-outer-container">
                <svg width="100" height="100" viewBox="0 0 42 42" className="custom-donut-chart">
                  <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#1c1f26" strokeWidth="4" />
                  {/* Green segment ~66% */}
                  <circle
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke="#10b981"
                    strokeWidth="4.5"
                    strokeDasharray="66 34"
                    strokeDashoffset="25"
                  />
                  {/* Purple segment ~24% */}
                  <circle
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke="#8b5cf6"
                    strokeWidth="4.5"
                    strokeDasharray="24 76"
                    strokeDashoffset="-41"
                  />
                  {/* Grey segment ~10% */}
                  <circle
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke="#6b7280"
                    strokeWidth="4.5"
                    strokeDasharray="10 90"
                    strokeDashoffset="-65"
                  />
                </svg>
                <Box className="donut-center-overlay">
                  <span className="donut-value">--</span>
                  <span className="donut-sub">detections</span>
                </Box>
              </Box>

              {/* Legend */}
              <Box className="legend-list-container">
                <ul className="legend-items">
                  <li>
                    <span className="dot dot-grey" />--</li>
                </ul>
              </Box>
            </Box>

            {/* Conservation Status Pills */}
            <Box className="conservation-pills-row">
              <span className="conservation-pill grey">
                LEAST CONCERN (--)
              </span>
              <span className="conservation-pill grey">
                VULNERABLE (--)
              </span>
              <span className="conservation-pill grey">
                UNKNOWN (--)
              </span>
            </Box>

            {/* <button
              type="button"
              className="go-deepdive-link"
              onClick={() => navigate(`/project/${projectDetails.project_id}/bio-diversity-dashboard`)}
            >
              Go to Deep-Dive &rarr;
            </button> */}
          </Box>

          {/* ── 3. ECOSYSTEM HEALTH INTELLIGENCE ── */}
          <Box className="nature-section ecosystem-health-section">
            <Box className="section-title-row">
              <span className="title-text">ECOSYSTEM HEALTH INTELLIGENCE</span>
              <HelpOutline
                className="info-icon"
                titleAccess="Ecosystem health analytics based on remote sensing indices"
              />
            </Box>

            <Box className="eco-health-grid">
              {/* Habitat Health Index */}
              <Box className="eco-metric-card orange-theme">
                <Box className="card-top">
                  <Park className="card-icon" />
                  <span className="card-label">HABITAT HEALTH INDEX</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,10 Q15,4 30,11 T60,5 T90,9 T100,7" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>

              {/* Veg. Difference Index */}
              <Box className="eco-metric-card green-theme">
                <Box className="card-top">
                  <Spa className="card-icon" />
                  <span className="card-label">VEG. DIFFERENCE INDEX</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,12 Q20,3 40,8 T80,10 T100,5" fill="none" stroke="#10b981" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>

              {/* Landscape Frag. Index */}
              <Box className="eco-metric-card orange-theme">
                <Box className="card-top">
                  <Terrain className="card-icon" />
                  <span className="card-label">LANDSCAPE FRAG. INDEX</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,6 Q10,12 25,6 T50,8 T75,4 T100,10" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>

              {/* Mean Species Abundance */}
              <Box className="eco-metric-card orange-theme">
                <Box className="card-top">
                  <Pets className="card-icon" />
                  <span className="card-label">MEAN SPECIES ABUNDANCE</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,12 Q20,6 40,11 T80,8 T100,13" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>

              {/* Human Disturbance Index */}
              <Box className="eco-metric-card green-theme">
                <Box className="card-top">
                  <WarningAmber className="card-icon" />
                  <span className="card-label">HUMAN DISTURBANCE INDEX</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,5 Q30,14 60,6 T100,4" fill="none" stroke="#10b981" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>

              {/* eDNA Detection Rate */}
              <Box className="eco-metric-card green-theme">
                <Box className="card-top">
                  <Fingerprint className="card-icon" />
                  <span className="card-label">EDNA DETECTION RATE</span>
                </Box>
                <span className="card-value">--</span>
                <Box className="card-spark-wave">
                  <svg viewBox="0 0 100 15" width="100%" height="15" preserveAspectRatio="none">
                    <path d="M0,10 Q20,2 50,11 T100,3" fill="none" stroke="#10b981" strokeWidth="1.5" />
                  </svg>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Climate Tab details */}
      {activeRightTab === 'climate' && climateCoords && (
        <LiveClimateCard lat={climateCoords.lat} lng={climateCoords.lng} />
      )}

      {activeRightTab === 'climate' && isTataMotors && (
        <Box className="nature-section" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', mt: 2 }}>
          {TATA_CLIMATE_METRICS.map(n => {
            const r = getMetricIndicator(n.real);
            if (!r) return null;
            return <TataIndicatorCard row={{ ...r, name: n.display }} abbr={n.abbr} description={METRIC_DESCRIPTIONS[n.real]} key={n.display} />;
          })}
        </Box>
      )}
    </Box>
  );
};

export default RightPanel;
